import axios from 'axios'
import { useState } from 'react'
import { log, LogCategories } from '../../utils/logger'

export type MediaTypesType = 'song' | 'stem' | 'sample' | 'genesis-song'
export const MediaTypes: { [key: string]: MediaTypesType | string } = {
  SONG: 'song',
  STEM: 'stem',
  SAMPLE: 'sample',
  SONGS: 'songs',
  STEMS: 'stems',
  SAMPLES: 'samples',
  GENESISSONG: 'genesis-song'
}

// Fixes javascript's garbo modulo (has issues with negative numbers)
export const modulo = (a, b) => {
  return ((a % b) + b) % b
}

// Segment event for where the open in studio button is clicked
export const segmentTrackRemix = (type, index, location) => {
  let source = window.location.pathname.includes('explore')
    ? '/explore'
    : window.location.pathname.includes('home')
    ? '/home'
    : window.location.pathname.includes('profile')
    ? '/profile'
    : window.location.pathname.includes('packs')
    ? '/sample-packs'
    : 'unknown'

  window.analytics.track('Button Clicked', {
    button: 'remix',
    source: source,
    location: location,
    type: type,
    id: index
  })
}

// Prints a wallet address in a shortened way so that it doesn't take us too much space
// (e.g. 0x213...A7b)
export const prettyPrintWalletAddress = (wallet_address) => {
  return (
    wallet_address.substring(0, 7) +
    '...' +
    wallet_address.substring(wallet_address.length - 3, wallet_address.length)
  )
}

export const getGenesisSongOwner = async (tokenId) => {
  return (await axios.get('/api/v1/song-genesis/owner', { params: { tokenId } })).data
}

// Returns the loop length in seconds as a decimal
export const getSongLengthInSecondsFromMetadata = (metadata) => {
  // getting the tempo
  let tempo = null
  for (let i = 0; i < metadata['attributes'].length; i++) {
    let attribute = metadata['attributes'][i]
    if (attribute.value.includes('Tempo: ')) {
      tempo = attribute.value.split('Tempo: ')[1]
      break
    }
  }
  if (tempo === null) return null

  // Songs are fixed at 256 beats, so converting the tempo to length
  // TODO: change this for variable length songs, or write new function for non-genesis songs
  return (60.0 / tempo) * 256
}

export const userBrowser = () => {
  const agentStr = navigator.userAgent
  if (agentStr.indexOf('Chrome') > -1) return 'chrome'
  else if (agentStr.indexOf('Firefox') > -1) return 'firefox'
  else if (agentStr.indexOf('Safari') > -1) return 'safari'
  else if (agentStr.indexOf('OP') > -1) return 'opera'

  return null
}

export const userOperatingSystem = () => {
  let os = ''
  if (navigator.userAgent.indexOf('Win') !== -1) os = 'Windows'
  if (navigator.userAgent.indexOf('Mac') !== -1) os = 'MacOS'
  if (navigator.userAgent.indexOf('X11') !== -1) os = 'UNIX'
  if (navigator.userAgent.indexOf('Linux') !== -1) os = 'Linux'
  return os
}

export const setCookie = (key, value, exhours = 24) => {
  log.info({ category: LogCategories.AUTH, data: { key, value, exhours } }, 'setCookie')

  const d = new Date()
  d.setTime(d.getTime() + exhours * 60 * 60 * 1000)
  let expires = 'expires=' + d.toUTCString()
  try {
    document.cookie = key + '=' + value + ';secure;SameSite=Strict;' + expires + ';path=/'
  } catch {
    console.log('cannot access document cookies')
  }
}

export const getCookie = (key) => {
  log.info({ category: LogCategories.AUTH, data: { key } }, 'getCookie - start')

  let name = key + '='
  let decodedCookie
  try {
    decodedCookie = decodeURIComponent(document.cookie)
  } catch {
    console.log('cannot access document cookies')
    return null
  }

  let ca = decodedCookie.split(';')

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') {
      c = c.substring(1)
    }
    if (c.indexOf(name) === 0) {
      const data = c.substring(name.length, c.length)

      log.info({ category: LogCategories.AUTH, data }, 'getCookie - success')

      return data
    }
  }
  return null
}

export const deleteCookie = (key) => {
  log.info({ category: LogCategories.AUTH, data: { key } }, 'deleteCookie')

  setCookie(key, '', -1)
}

export const copyToClipboard = async (text) => {
  if ('clipboard' in navigator) {
    return await navigator.clipboard.writeText(text)
  } else {
    return document.execCommand('copy', true, text)
  }
}

export const defaultProfPic = () => {
  return '/static/images/default_prof_pic.svg'
}

export const titleCase = (str) => {
  return str
    .split(' ')
    .map((w) => w[0].toUpperCase() + w.substring(1).toLowerCase())
    .join(' ')
}

export const clampToValue = (value: number, clampToValue: number): number => {
  return Math.floor(value / clampToValue) * clampToValue
}

export const clampToRoundValue = (value: number, clampToValue: number): number => {
  return Math.round(value / clampToValue) * clampToValue
}

export type Status = 'loading' | 'ready' | 'error'
export const useStatus = (initial: Status = 'loading') => useState<Status>(initial)
